import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Settings, Activity, Radar, CircleDot, LogOut } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/aktivite", label: "Aktivite", icon: Activity, testid: "nav-activity" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    refetchInterval: 8000,
  });

  const connected = settings?.apiKeySet;
  const autoOn = settings?.automationEnabled;

  return (
    <div className="min-h-screen grid-backdrop text-foreground flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-zinc-800 bg-zinc-950/70 backdrop-blur-xl flex flex-col fixed h-screen z-20">
        <div className="px-5 py-5 border-b border-zinc-800">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center">
              <Radar className="h-5 w-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold tracking-tight text-[14px]">GPS-Vehiclize</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Otomasyon</div>
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1 flex-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-blue-600 text-white font-medium"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-zinc-800 space-y-2.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-zinc-500">Bağlantı</span>
            <span className={`flex items-center gap-1.5 font-medium ${connected ? "text-emerald-400" : "text-zinc-500"}`}>
              <CircleDot className="h-3 w-3" /> {connected ? "Bağlı" : "Bağlı Değil"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-500">Otomasyon</span>
            <span className={`flex items-center gap-1.5 font-medium ${autoOn ? "text-blue-400" : "text-zinc-500"}`}>
              <span className={`h-2 w-2 rounded-full ${autoOn ? "bg-blue-500 live-dot" : "bg-zinc-600"}`} />
              {autoOn ? "Aktif" : "Kapalı"}
            </span>
          </div>
          <div className="text-[10px] text-zinc-600 pt-1 font-mono uppercase">
            {settings?.environment || "staging"}
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors"
          >
            <span className="flex items-center gap-2 truncate">
              <LogOut className="h-4 w-4" />
              <span className="truncate">{user?.name || "Yönetici"}</span>
            </span>
            <span className="text-[10px] uppercase tracking-wider text-zinc-600">Çıkış</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
